import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, ShieldCheck } from "lucide-react";
import { useAssessment } from "@/context/AssessmentContext";

const QUESTIONS = [
  { id: "radicular", text: "Numbness, tingling or weakness shooting down your arms or legs, or dropping things a lot?" },
  { id: "myelopathy", text: "Trouble walking straight, or losing control of your bladder/bowels, or numbness in the groin area?" },
  { id: "cranial", text: "A sudden, worst-ever headache, blackouts, spinning dizziness, or trouble swallowing?" },
  { id: "trauma", text: "A recent bad fall, car crash, or hard hit to your back or neck?" },
  { id: "systemic", text: "Unexplained weight loss, night sweats, or pain that won't go away even when resting?" },
  { id: "pregnancy_disease", text: "Do you have osteoporosis, a history of cancer, or a fever right now?" },
];

export default function SafetyScreen() {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const { update } = useAssessment();
  const navigate = useNavigate();

  const hasRedFlag = Object.values(answers).some((v) => v === true);
  const answered = QUESTIONS.every((q) => answers[q.id] !== undefined);

  const proceed = () => {
    update("safety", { answers, cleared: !hasRedFlag });
    setSubmitted(true);
    if (!hasRedFlag) setTimeout(() => navigate("/assessment"), 700);
  };

  return (
    <div className="max-w-3xl mx-auto animate-fade-in">
      <div className="flex items-center gap-2 mb-4 text-sm text-forest font-medium">
        <ShieldCheck className="w-4 h-4" /> Step 1 · Quick Safety Check
      </div>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-forest mb-3">
        Let's make sure it's safe to check yourself
      </h1>
      <p className="text-muted-foreground mb-8 leading-relaxed">
        Before we look at your posture, we need to make sure none of these serious symptoms are present.
        Answer honestly — a "Yes" doesn't mean something bad, just that a doctor should look first.
      </p>

      <div className="bg-white border border-forest/10 rounded-xl divide-y divide-forest/10">
        {QUESTIONS.map((q) => (
          <div key={q.id} className="p-5 flex items-start gap-4">
            <p className="flex-1 text-sm sm:text-base leading-relaxed">{q.text}</p>
            <div className="flex gap-2">
              {[
                { label: "No", val: false },
                { label: "Yes", val: true },
              ].map((opt) => (
                <button
                  key={opt.label}
                  data-testid={`safety-${q.id}-${opt.label.toLowerCase()}`}
                  onClick={() => setAnswers((a) => ({ ...a, [q.id]: opt.val }))}
                  className={`px-4 py-1.5 text-sm rounded-full border transition-colors ${
                    answers[q.id] === opt.val
                      ? opt.val
                        ? "bg-terracotta text-white border-terracotta"
                        : "bg-forest text-bone border-forest"
                      : "border-forest/20 hover:border-forest/40"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {submitted && hasRedFlag && (
        <Alert className="mt-6 border-terracotta bg-terracotta/10" data-testid="redflag-alert">
          <AlertTriangle className="w-5 h-5 text-terracotta" />
          <AlertTitle className="text-terracotta font-heading">Please see a doctor first</AlertTitle>
          <AlertDescription className="text-ink mt-2">
            Based on your answers, you should see a doctor in person before doing any self-assessment.
            These symptoms need a proper hands-on check — please don't skip it.
          </AlertDescription>
        </Alert>
      )}

      <div className="mt-8 flex justify-end">
        <Button
          onClick={proceed}
          disabled={!answered}
          className="bg-forest hover:bg-forest/90 text-bone rounded-full px-7"
          data-testid="safety-continue-btn"
        >
          I'm safe — continue
        </Button>
      </div>
    </div>
  );
}
